import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { MagnifyingGlass, Fire } from "@phosphor-icons/react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import BlogNewsletter from "@/components/BlogNewsletter";
import { fetchBlogPosts, fetchBlogTags, trackTagClick } from "@/lib/api";
import { BLOG } from "@/constants/testIds";
import { CANONICAL_ORIGIN } from "@/lib/seo";

const BlogList = () => {
  const [posts, setPosts] = useState([]);
  const [tags, setTags] = useState([]);
  const [activeTag, setActiveTag] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBlogTags().then(setTags).catch(() => setTags([]));
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setLoading(true);
    (async () => {
      try {
        const data = await fetchBlogPosts(1, { tag: activeTag, search });
        setPosts(data.posts);
      } finally {
        setLoading(false);
      }
    })();
  }, [activeTag, search]);

  return (
    <div className="relative min-h-screen text-white">
      <Helmet>
        <title>Growth & ASO Blog | Skilmark</title>
        <meta
          name="description"
          content="Fresh app marketing, ASO, and viral video growth tactics — new insights published daily by Skilmark's AI CMO engine."
        />
        <link rel="canonical" href={`${CANONICAL_ORIGIN}/blog`} />
        <meta property="og:title" content="Growth & ASO Blog | Skilmark" />
        <meta property="og:description" content="Fresh app marketing, ASO, and viral video growth tactics, published daily." />
        <meta property="og:type" content="website" />
      </Helmet>

      <Navbar />

      <div className="pt-32 pb-24 px-6 lg:px-12 max-w-6xl mx-auto" data-testid="blog-list-page">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#7C93B8] mb-4">Skilmark Blog</p>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight mb-6 max-w-2xl">
          Growth tactics, ASO insights, and viral hooks — fresh every day.
        </h1>
        <p className="text-base text-white/60 leading-relaxed max-w-3xl mb-4">
          This blog is written entirely by Skilmark's autonomous AI CMO — the same growth engine that generates ad copy,
          ASO metadata, and video scripts for the apps on our platform. Every post below documents a real tactic, a
          real experiment, or a real lesson pulled straight from campaigns we've actually run: what made a hook go
          viral, why a particular App Store title outranked its competitors, or how a single email sequence cut churn
          by double digits. Nothing here is theoretical marketing advice recycled from a textbook.
        </p>
        <p className="text-base text-white/60 leading-relaxed max-w-3xl mb-10">
          Browse by category to go deeper: <span className="text-white/80">App Store Optimization</span> covers
          keyword research, title/subtitle testing, and conversion-rate tactics for your store listing.{" "}
          <span className="text-white/80">Ad Copy & Creative</span> breaks down the hooks, scripts, and visuals that
          stop the scroll on Meta and Google. <span className="text-white/80">Growth Loops</span> looks at retention,
          referral, and lifecycle email/SMS sequences that compound over time. And{" "}
          <span className="text-white/80">Viral Video</span> is where we reverse-engineer why certain short-form ad
          creatives outperform the rest. New posts publish automatically every day — use the tag filters or search
          below to find what's relevant to your app right now.
        </p>

        <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-6">
          <div className="relative flex-1 max-w-sm">
            <MagnifyingGlass size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30" />
            <input
              data-testid={BLOG.searchInput}
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search posts..."
              className="w-full bg-white/5 border border-white/10 rounded-full pl-10 pr-4 py-2.5 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-white/60 transition-colors duration-300"
            />
          </div>
        </div>

        {tags.length > 0 && (
          <div className="flex gap-2 flex-wrap mb-12">
            <button
              data-testid={BLOG.tagChipAll}
              onClick={() => setActiveTag("")}
              className={`text-[11px] uppercase tracking-wider rounded-full border px-3 py-1.5 transition-colors duration-300 ${
                activeTag === "" ? "border-white bg-white/10 text-white" : "border-white/15 text-white/40 hover:text-white/70"
              }`}
            >
              All
            </button>
            {tags.map(({ tag, count, trending }) => (
              <button
                key={tag}
                data-testid={BLOG.tagChip(tag)}
                onClick={() => {
                  setActiveTag(tag === activeTag ? "" : tag);
                  trackTagClick(tag);
                }}
                className={`text-[11px] uppercase tracking-wider rounded-full border px-3 py-1.5 transition-colors duration-300 flex items-center gap-1.5 ${
                  activeTag === tag ? "border-white bg-white/10 text-white" : "border-white/15 text-white/40 hover:text-white/70"
                }`}
              >
                {trending && <Fire data-testid={BLOG.trendingBadge(tag)} size={11} weight="fill" className="text-white" />}
                {tag} <span className="text-white/30">({count})</span>
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <p className="text-white/40 text-sm">Loading posts...</p>
        ) : posts.length === 0 ? (
          <p data-testid={BLOG.emptyState} className="text-white/40 text-sm">
            No posts match your search. Try a different keyword or tag.
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {posts.map((post, i) => (
              <motion.div
                key={post.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: (i % 3) * 0.08 }}
              >
                <Link
                  to={`/blog/${post.slug}`}
                  data-testid={`blog-post-card-${post.slug}`}
                  className="tracing-border glass-panel rounded-2xl overflow-hidden flex flex-col h-full hover:-translate-y-1 transition-transform duration-300"
                >
                  {post.cover_image_url && (
                    <div className="aspect-[16/9] overflow-hidden">
                      <img src={post.cover_image_url} alt={post.cover_image_alt || post.title} className="w-full h-full object-cover" />
                    </div>
                  )}
                  <div className="p-6 flex flex-col flex-1">
                    <div className="flex gap-2 mb-3 flex-wrap">
                      {(post.tags || []).slice(0, 2).map((t) => (
                        <span key={t} className="text-[10px] uppercase tracking-wider rounded-full border border-white/15 px-2.5 py-0.5 text-white/40">
                          {t}
                        </span>
                      ))}
                    </div>
                    <h2 className="font-display text-lg font-bold mb-2 leading-snug">{post.title}</h2>
                    <p className="text-sm text-white/50 leading-relaxed line-clamp-3 mb-4">{post.excerpt}</p>
                    <span className="text-xs text-white/30 mt-auto">
                      {new Date(post.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                    </span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}

        <BlogNewsletter />
      </div>

      <Footer />
    </div>
  );
};

export default BlogList;
